<template>
    <l-card-content>
        <l-headline
            :tabs="tabs"
            :title="$t('surveys.details.titleNameOwners')"
            :sub-title="survey.name ? survey.name : $t('surveys.details.labelNewSurvey')"
            :auto-save-text="autoSavingText"
            btn-test-id="surveys-create"
        >
        </l-headline>

        <hr class="visible-xs" />

        <l-input-text
            v-model.trim="survey.name"
            :label="``"
            :placeholder="$t(`surveys.details.helpName`)"
            :debounce-delay="1000"
            data-test-id="survey-edit-name"
            @input="debouncedUpdateSurvey()"
        ></l-input-text>

        <l-rich-text-editor
            v-model.trim="survey.description"
            data-test-id="survey-edit-description"
            :content="survey.description || ''"
            :placeholder="$t('surveys.details.helpDescription')"
            @input="debouncedUpdateSurvey()"
        ></l-rich-text-editor>

        <hr />

        <div
            v-for="owner in survey.owners"
            :key="owner._id"
            data-test-class="survey-edit-existing-owners"
            class="panel panel-default"
        >
            <div class="panel-heading">
                <div class="row">
                    <div class="col-md-11">
                        <span>{{
                            $t("surveys.details.labelOwner", {
                                firstname: owner.firstname,
                                lastname: owner.lastname,
                            })
                        }}</span>
                    </div>
                    <div class="col-md-1">
                        <span
                            v-show="user._id != owner._id"
                            class="pull-right color-grey clickable"
                            tabindex="0"
                            @click="removeOwner(owner)"
                            @keyup.enter="removeOwner(owner)"
                        >
                            <i class="fal fa-user-minus color-primary"></i>
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Owners -->
        <l-input-typeahead
            :label="``"
            :placeholder="$t(`surveys.details.placeholderOwner`)"
            :data="ownerOptions"
            :on-select="typeaheadOwnerOnSelect"
            :on-input-change="typeaheadOwnerOnInputChange"
            data-test-id="surveys-create-owners"
        />

        <l-input-typeahead
            :label="``"
            :placeholder="$t(`goals.update.placeholderTag`)"
            :data="tags"
            :show-picture="false"
            :on-select="typeaheadTagOnSelect"
            :on-input-change="() => {}"
            data-test-id="surveys-create-tags"
        />

        <div v-show="survey.tags && survey.tags.length">
            <span
                v-for="tag in survey.tags"
                :key="tag.name"
                data-test-class="survey-edit-exitsing-tags"
            >
                <span
                    class="label label-light clickable"
                    style="margin-right: 9px;"
                    tabindex="0"
                    @click="removeTag(tag)"
                    @keyup.enter="removeTag(tag)"
                >
                    {{ tag.name }} <i class="fal fa-remove"></i>
                </span>
            </span>
        </div>

        <div v-show="survey.status != 'closed'">
            <hr />
            <div v-show="error && error.message" id="error" class="alert alert-info">
                <span>{{ error.message }}</span>
            </div>
            <div class="row">
                <div class="col-md-3 col-md-offset-9">
                    <button
                        class="btn btn-primary btn-raised btn-block"
                        type="button"
                        tabindex="0"
                        @click="$router.push(`/surveys/edit/${$route.params.id}/questions`)"
                    >
                        <span>{{ $t("surveys.details.btnSaveChanges") }}</span>
                    </button>
                </div>
            </div>
        </div>
    </l-card-content>
</template>

<script>
import { mapGetters } from "vuex";

import LCardContent from "@/components/LCardContent";
import LHeadline from "@/components/LHeadline";
import LInputText from "@/components/LInputText";
import LInputTypeahead from "@/components/LInputTypeahead";
import LRichTextEditor from "@/components/LRichTextEditor";

import icon from "@/img/icons/surveys.png";
import format from "@/utils/format-typeahead";
import debounce from "lodash.debounce";

export default {
    components: {
        LCardContent,
        LHeadline,
        LInputText,
        LInputTypeahead,
        LRichTextEditor,
    },
    props: {
        autoSavingText: {
            type: String,
            default: "",
        },
        openHelpHC: {
            type: Function,
            default: () => {},
        },
    },
    data() {
        return {
            searchKeyword: "",
            survey: {},
        };
    },
    computed: {
        ...mapGetters({
            user: "users/current/user",
            rights: "users/current/rights",

            tags: "tags/tags/surveys",
            usersSearchList: "users/search/list",
            getUserList: "users/list/list",

            getDetail: "surveys/surveys/detail",

            error: "surveys/surveys/error",
        }),
        tabs() {
            return [
                {
                    tabClass: "space",
                    show: true,
                },
                {
                    show: true,
                    label: this.$t("surveys.details.showHC"),
                    handler: this.openHelpHC,
                    isButton: true,
                },
            ];
        },
        ownerOptions() {
            let list = this.usersSearchList;
            if (!this.rights.S_ACCESS_ALL) {
                // survey admin can add self.
                list = list.filter(user => user._id !== this.user._id);
            }

            if (!list.length) {
                list = this.getUserList({ key: "directTeam" });
            }
            return list.map(format.users);
        },
    },
    watch: {
        "$route.params.id": {
            deep: true,
            immediate: true,
            handler(id) {
                this.survey = this.getDetail(id);
                if (!this.survey.owners?.length) {
                    this.survey.owners.push(this.user);
                }
            },
        },
    },

    created() {
        this.icon = icon;
    },
    methods: {
        typeaheadOwnerOnInputChange(query) {
            if (query.length >= 2) {
                this.$store.dispatch("users/search/findByName", { query });
            } else {
                this.$store.dispatch("users/search/reset");
            }
        },

        typeaheadOwnerOnSelect(selected) {
            if (!this.survey.owners.find(u => u._id === selected._id)) {
                this.survey.owners.push(selected);
            }
            this.updateSurvey();
        },
        removeOwner(owner) {
            if (owner._id === this.user._id) {
                return;
            }
            this.survey.owners = this.survey.owners.filter(o => o._id != owner._id);
            this.updateSurvey();
        },

        typeaheadTagOnSelect(selected) {
            if (!this.survey.tags) {
                this.survey.tags = [];
            }
            if (!this.survey.tags.find(u => u._id === selected._id)) {
                this.survey.tags.push(selected);
            }
            this.updateSurvey();
        },
        removeTag(tag) {
            this.survey.tags = this.survey.tags.filter(t => t._id != tag._id);
            this.updateSurvey();
        },
        debouncedUpdateSurvey: debounce(function() {
            this.updateSurvey();
        }, 1000),
        updateSurvey() {
            this.$emit("change", "name", this.survey);
        },
        goTo(url) {
            this.$router.push(url);
        },
    },
};
</script>

<style scoped></style>
